import { getAuthScheme, type LoginType } from '@/lib/auth'
import { LANGUAGE_STORAGE_KEY, DEFAULT_LANGUAGE } from '@/lib/i18n/translations'

const DEFAULT_LOGIN_TYPE: LoginType = 'local'

const getStoredAuthState = (): { token: string | null; loginType: LoginType } => {
  if (typeof window === 'undefined') {
    return { token: null, loginType: DEFAULT_LOGIN_TYPE }
  }

  const authStorage = localStorage.getItem('auth-storage')
  if (!authStorage) {
    return { token: null, loginType: DEFAULT_LOGIN_TYPE }
  }

  try {
    const { state } = JSON.parse(authStorage)
    return {
      token: state?.token ?? null,
      loginType: (state?.loginType ?? DEFAULT_LOGIN_TYPE) as LoginType
    }
  } catch (error) {
    console.error('Error parsing auth storage:', error)
    return { token: null, loginType: DEFAULT_LOGIN_TYPE }
  }
}

const getStoredLanguage = (): string => {
  if (typeof window === 'undefined') {
    return DEFAULT_LANGUAGE
  }
  return localStorage.getItem(LANGUAGE_STORAGE_KEY) ?? DEFAULT_LANGUAGE
}

const normalizeHeaders = (headers?: HeadersInit): Record<string, string> => {
  if (!headers) {
    return {}
  }

  if (headers instanceof Headers) {
    const result: Record<string, string> = {}
    headers.forEach((value, key) => {
      result[key] = value
    })
    return result
  }

  if (Array.isArray(headers)) {
    return headers.reduce((acc, [key, value]) => {
      acc[key] = value
      return acc
    }, {} as Record<string, string>)
  }

  return { ...headers }
}

export const buildFetchHeaders = (headers?: HeadersInit, method?: string): Record<string, string> => {
  const { token, loginType } = getStoredAuthState()
  const normalized = normalizeHeaders(headers)
  const normalizedMethod = method?.toUpperCase()
  const shouldIncludeContentType = !normalizedMethod || (normalizedMethod !== 'GET' && normalizedMethod !== 'HEAD')
  const authScheme = token ? getAuthScheme(loginType) : null
  const language = getStoredLanguage()

  return {
    ...(shouldIncludeContentType ? { 'Content-Type': 'application/json' } : {}),
    ...({
      'Source-App': 'haowhoo',
      'source': 'web',
      'Accept-Language': language,
    }),
    ...(token && authScheme ? { Authorization: `${authScheme} ${token}` } : {}),
    ...normalized
  }
}

export const fetchWithAuth = (url: RequestInfo, init: RequestInit = {}) => {
  const { headers, method, ...rest } = init
  return fetch(url, {
    method,
    ...rest,
    headers: buildFetchHeaders(headers, method)
  })
}
